import type { DependentField } from '@hubspot/api-client/lib/codegen/marketing/forms'
import type { DependentFieldFilter } from '@hubspot/api-client/lib/codegen/marketing/forms/models/DependentFieldFilter'

type Operator = DependentFieldFilter['operator']

type SingleValueOperator = Extract<Operator, 'eq' | 'neq' | 'contains' | 'doesnt_contain' | 'str_starts_with' | 'str_ends_with' | 'lt' | 'lte' | 'gt' | 'gte'>

type SingleValuePredicate = (value: string | number | undefined, conditionalValue: string) => boolean

const SINGLE_VALUE_PREDICATES = {
  eq: (value, conditionalValue) => value === conditionalValue,
  neq: (value, conditionalValue) => value !== conditionalValue,
  contains: (value, conditionalValue) => String(value).includes(conditionalValue),
  doesnt_contain: (value, conditionalValue) => !String(value).includes(conditionalValue),
  str_starts_with: (value, conditionalValue) => String(value).startsWith(conditionalValue),
  str_ends_with: (value, conditionalValue) => String(value).endsWith(conditionalValue),
  lt: (value, conditionalValue) => Number(value) < Number(conditionalValue),
  lte: (value, conditionalValue) => Number(value) <= Number(conditionalValue),
  gt: (value, conditionalValue) => Number(value) > Number(conditionalValue),
  gte: (value, conditionalValue) => Number(value) >= Number(conditionalValue),
} satisfies Record<SingleValueOperator, SingleValuePredicate>

type RangeValueOperator = Extract<Operator, 'between' | 'not_between' | 'within_time_reverse' | 'within_time'>

type RangeValuePredicate = (value: string | number | undefined, rangeStart: string, rangeEnd: string) => boolean

const RANGE_VALUE_PREDICATES = {
  between: (value, rangeStart, rangeEnd) => Number(value) >= Number(rangeStart) && Number(value) <= Number(rangeEnd),
  not_between: (value, rangeStart, rangeEnd) => Number(value) < Number(rangeStart) || Number(value) > Number(rangeEnd),
  within_time: (value, rangeStart, rangeEnd) => {
    const dateValue = new Date(value ?? 0)
    const startDate = new Date(rangeStart)
    const endDate = new Date(rangeEnd)
    return dateValue >= startDate && dateValue <= endDate
  },
  within_time_reverse: (value, rangeStart, rangeEnd) => {
    const dateValue = new Date(value ?? 0)
    const startDate = new Date(rangeStart)
    const endDate = new Date(rangeEnd)
    return dateValue <= startDate && dateValue >= endDate
  },
} satisfies Record<RangeValueOperator, RangeValuePredicate>

type MultiValueOperator = Extract<Operator, 'set_any' | 'set_not_any' | 'set_all' | 'set_not_all' | 'set_eq' | 'set_neq'>

type MultiValuePredicate = (values: string[], conditionalValues: string[]) => boolean

const set_any: MultiValuePredicate = (values, conditionalValues) => values.some(it => conditionalValues.includes(it))

const set_all: MultiValuePredicate = (values, conditionalValues) => values.every(it => conditionalValues.includes(it))

const set_eq: MultiValuePredicate = (values, conditionalValues) => JSON.stringify([...values].sort()) === JSON.stringify([...conditionalValues].sort())

const MULTI_VALUE_PREDICATES = {
  set_any,
  set_not_any: (values, conditionalValues) => !set_any(values, conditionalValues),
  set_all,
  set_not_all: (values, conditionalValues) => !set_all(values, conditionalValues),
  set_eq,
  set_neq: (values, conditionalValues) => !set_eq(values, conditionalValues),
} satisfies Record<MultiValueOperator, MultiValuePredicate>

/**
 * Function to determine if dependent fields should be shown based on the values provided.
 */
export function showDependentField(singleValue: string | number | undefined, multipleValues: string[]) {
  return ({ dependentCondition }: DependentField): boolean => {
    if (dependentCondition.operator === 'is_not_empty') {
      return String(singleValue).length > 0 || multipleValues.length > 0
    }
    else if (dependentCondition.operator in MULTI_VALUE_PREDICATES) {
      const predicate = MULTI_VALUE_PREDICATES[dependentCondition.operator as MultiValueOperator]
      return predicate(multipleValues, dependentCondition.values)
    }
    else if (dependentCondition.operator in SINGLE_VALUE_PREDICATES) {
      const predicate = SINGLE_VALUE_PREDICATES[dependentCondition.operator as SingleValueOperator]
      return predicate(singleValue, dependentCondition.value)
    }
    else if (dependentCondition.operator in RANGE_VALUE_PREDICATES) {
      const predicate = RANGE_VALUE_PREDICATES[dependentCondition.operator as RangeValueOperator]
      return predicate(singleValue, dependentCondition.rangeStart, dependentCondition.rangeEnd)
    }

    return false
  }
}
